import { Box, Input, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "../Modal";
import SearchFilteredResults from "./components/SearchFilteredResults";
import SearchVisibleFilter from "./components/SearchVisibleFilter";
import { filterByDecade } from "./functions/search-modal-functions";

import { IconSearch } from "@/components/icons";
import { useMarketDecades } from "@/services/market/get-decades";
import { useMarketMakes } from "@/services/market/get-makes";
import { useSearchMarketCars } from "@/services/market/search-market-cars";
import { MarketCar } from "@/types/market";

import type { FC } from "react";

interface SearchModalProps {
  opened: boolean;
  open: () => void;
  close: () => void;
}

const SearchModal: FC<SearchModalProps> = ({ opened, close }) => {
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement | null>(null);

  const { data: makes = [], isPending: isMakesLoading } = useMarketMakes();

  const makeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    makes.forEach((make) => {
      map.set(make.id, make.name);
    });
    return map;
  }, [makes]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debounced] = useDebouncedValue(searchTerm, 400);
  const [makeFilter, setMakeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string[]>([]);
  const [filteredResults, setFilteredResults] = useState<MarketCar[]>([]);
  const [visibleResultsCount, setVisibleResultsCount] = useState(10);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const { isPending: isDecadesLoading } = useMarketDecades();

  const isDisabled =
    (debounced.length === 0 && makeFilter.length === 0 && yearFilter.length === 0) ||
    isMakesLoading ||
    isDecadesLoading;

  const {
    mutate: searchCars,
    data: searchResults,
    isPending,
    error,
    reset,
  } = useSearchMarketCars();

  useEffect(() => {
    if (opened) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [opened]);

  // Updates the filtered results whenever search results, filters, or search term change
  useEffect(() => {
    if (!searchResults?.results) {
      setFilteredResults([]);
      return;
    }

    // Show filters when results arrive (instead of requiring click)
    setFiltersVisible(true);

    // Start from server results. Server already performs the full_text search,
    // so avoid re-applying a strict client-side text filter which can exclude
    // items the backend returned (causing empty UI results despite network hits).
    let results = [...searchResults.results];
    if (makeFilter.length) {
      results = results.filter((car) => makeFilter.includes(car.make_id));
    }
    if (yearFilter.length > 0) {
      results = filterByDecade(results, yearFilter);
    }

    setFilteredResults(results);
    setVisibleResultsCount(10);
  }, [searchResults, debounced, makeFilter, statusFilter, yearFilter, makeNameMap]);

  useEffect(() => {
    if (
      !loadMoreRef.current ||
      !resultsContainerRef.current ||
      visibleResultsCount >= filteredResults.length
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleResultsCount((current) => Math.min(current + 10, filteredResults.length));
        }
      },
      {
        root: resultsContainerRef.current,
        rootMargin: "100px",
      }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [filteredResults.length, visibleResultsCount]);

  // Handles search input changes: triggers API search only when there's text
  useEffect(() => {
    if (debounced.trim().length === 0) {
      reset();
      return;
    }

    // Ensure filters appear once the user has triggered a search
    setFiltersVisible(true);
    searchCars({ data: { full_text: debounced } });
  }, [debounced, searchCars, reset]);

  return (
    <Modal
      opened={opened}
      size="lg"
      onClose={() => {
        close();
        setSearchTerm("");
        setMakeFilter([]);
        setStatusFilter(null);
        setYearFilter([]);
        setFilteredResults([]);
        setVisibleResultsCount(10);
        setFiltersVisible(false);
        reset();
      }}
      title={t("common.search")}
      icon={IconSearch}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box style={{ flexShrink: 0 }}>
          <Input
            ref={inputRef}
            placeholder={t("components.header.search-placeholder")}
            value={searchTerm}
            onClick={() => !filtersVisible && setFiltersVisible(true)}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setFilteredResults([]);
              setVisibleResultsCount(10);
            }}
            leftSection={<IconSearch size={16} />}
            mt={"sm"}
            mb="md"
          />

          {filtersVisible && (
            <SearchVisibleFilter
              debounced={debounced}
              isDisabled={isDisabled}
              makeFilter={makeFilter}
              setFilteredResults={setFilteredResults}
              setMakeFilter={setMakeFilter}
              setSearchTerm={setSearchTerm}
              setVisibleResultsCount={setVisibleResultsCount}
              setYearFilter={setYearFilter}
              yearFilter={yearFilter}
            />
          )}

          {isPending && (
            <Text c="dimmed" mt="sm" fz="sm">
              {t("components.header.loading_search")}..
            </Text>
          )}
          {error && debounced.trim().length > 0 && (
            <Text c="red" mt="sm" fz="sm">
              {t("components.header.error_search")}
            </Text>
          )}
        </Box>

        {filteredResults.length > 0 && (
          <SearchFilteredResults
            filteredResults={filteredResults}
            resultsContainerRef={resultsContainerRef}
            loadMoreRef={loadMoreRef}
            visibleResultsCount={visibleResultsCount}
            close={close}
          />
        )}

        {filtersVisible && debounced && !isPending && filteredResults.length === 0 && (
          <Text c="dimmed" mt="md" size="sm">
            {t("components.header.no_results")}
          </Text>
        )}

        {filtersVisible && debounced.length < 1 && !isPending && filteredResults.length === 0 && (
          <Text c="dimmed" mt="md" size="sm">
            {t("components.header.filter.no_active_search")}
          </Text>
        )}
      </Box>
    </Modal>
  );
};

export default SearchModal;
